import { YouButton } from '@/components/YouButton';
import { useEffect, useState } from 'react';
import { channelPairs, onePerCustomer } from '@/lib/channels';
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { useKept, useKeptScroll } from '@/lib/keptScroll';
import { useBackStep } from '@/lib/backStep';
import { useAuth } from '@/lib/auth';
import { useChats } from '@/data/store';
import { EMBEDDED, STANDALONE_CHATS_URL, isPhone, useDevice, useWide } from '@/lib/device';
import type { Chat } from '@/data/types';
import { AppsIcon, ArchiveIcon, BellIcon, BackIcon, CloseIcon, FilterIcon, SearchIcon } from '@/components/Icons';
import { SENTINEL_WINDOW, openInSentinel, portalUrl, sentinelPathFor } from '@/lib/contact';
import { FilterSheet } from '@/components/FilterSheet';
import { clearFilter, filterCount, passesFilter, toggleFilter, useListFilter } from '@/lib/listFilter';
import { ChatRows, SelectionBar, ordered, useSelection } from '@/components/ChatListParts';
import { useOverlay } from '@/components/Overlay';
import { seesChat } from '@/lib/chatGroups';
import { ReportButton } from '@/components/ReportButton';
import { AskButton } from '@/components/AskButton';
import { DutySwitch } from '@/components/DutySwitch';
import { todayIso, useReminders } from '@/lib/reminders';

// No tabs above the list for now (Gary, 23 Oct: confusing): every open chat shows, ordered as before.

function matches(c: Chat, q: string): boolean {
  const term = q.toLowerCase();
  const digits = term.replace(/\D/g, '');
  return c.name.toLowerCase().includes(term)
    || c.area.toLowerCase().includes(term)
    || (digits.length >= 3 && (c.phone ?? '').replace(/\D/g, '').includes(digits))
    || c.thread.some((m) => 'body' in m && m.body.kind === 'text' && m.body.text.toLowerCase().includes(term));
}

export function ChatListScreen() {
  const listScroll = useKeptScroll<HTMLElement>('chats');
  const navigate = useNavigate();
  const { access, session } = useAuth();
  const { live: liveData, chats } = useChats();
  const { standalone, notify, askNotifications } = useDevice();
  const selection = useSelection();
  const [search, setSearch] = useKept<string | null>('chats.search', null);
  // The phone's Back closes the search and stays on the list (mobile review, 19 Oct).
  useBackStep('chatsSearch', search !== null, () => setSearch(null));
  const wide = useWide();
  // An open sheet takes Escape and the arrows for itself.
  const { open: sheetOpen, openSheet, toast } = useOverlay();
  const openId = useMatch('/chat/:id/*')?.params.id;

  // From Sentinel's Chats switch (Gary, 18 Oct): ?customer= or ?lead= opens that person's latest chat.
  const [params, setParams] = useSearchParams();
  const wantCustomer = params.get('customer');
  const wantLead = params.get('lead');
  useEffect(() => {
    if ((!wantCustomer && !wantLead) || !liveData || !chats.length) return;
    const theirs = chats.filter((c) => (wantCustomer && c.customerId === wantCustomer) || (wantLead && c.leadId === wantLead))
      .sort((a, b) => Number(!!a.closed) - Number(!!b.closed) || (b.lastActivityAt ?? '').localeCompare(a.lastActivityAt ?? ''));
    setParams({}, { replace: true });
    if (theirs[0]) navigate(`/chat/${theirs[0].id}`);
    else toast('No WhatsApp chat with this customer yet. Start one with New chat.');
  }, [wantCustomer, wantLead, liveData, chats, navigate, setParams, toast]);

  // Everything this person may see, archived or not.
  const visible = chats.filter((c) => seesChat(c, access, session?.user.id));
  // The filter (status, owner, area, labels) narrows everything below it, counts included (Gary, 18 Oct).
  const listFilter = useListFilter();
  // A chat selected under one tab or filter never stays selected out of sight (mobile review, 19 Oct); select
  // mode itself stays on.
  const narrow = (fn: () => void) => () => { selection.unpick(); fn(); };
  const now = Date.now();
  const unfiltered = visible.filter((c) => !c.archived);
  // One row per customer (Gary, 19 Oct): their WhatsApp and email chats show once, the latest standing for both.
  const pairs = channelPairs(visible);
  const live = unfiltered.filter((c) => passesFilter(c, listFilter));
  const filterOn = filterCount(listFilter) > 0;
  const archived = visible.filter((c) => c.archived);
  // Reminders above Archived (Gary, 3 Nov 2026): how many, in the accent colour once one is due.
  const { reminders } = useReminders(liveData);
  const remindersDue = reminders.filter((r) => r.date <= todayIso()).length;

  let list: Chat[];
  if (search !== null && search.trim()) {
    // Closed or archived results have nothing to read, as in Archived (Gary, 19 Oct).
    list = onePerCustomer(visible.filter((c) => matches(c, search.trim())).map((c) => (c.archived || c.closed ? { ...c, unread: 0, markedUnread: false } : c)), pairs, now);
  } else {
    // One row per customer (review, 19 Oct).
    const rows = onePerCustomer(live, pairs, now);
    list = rows;
  }
  list = ordered(list);


  // Desktop: up and down arrows move through the list, Escape closes the chat.
  useEffect(() => {
    if (!wide) return;
    const ids = list.map((c) => c.id);
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (sheetOpen || (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable))) return;
      if (e.key === 'Escape' && openId) { navigate('/'); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const at = openId ? ids.indexOf(openId) : -1;
      const next = ids[e.key === 'ArrowDown' ? Math.min(ids.length - 1, at + 1) : Math.max(0, at - 1)];
      if (next && next !== openId) navigate(`/chat/${next}`);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  const preview = import.meta.env.DEV && sessionStorage.getItem('chats.preview') !== null;
  const phoneProblem = isPhone && !preview && (!standalone || notify !== 'granted');
  // A computer is never asked at setup, so the list asks once (Gary, 15 Oct).
  const [deskAsked, setDeskAsked] = useState(() => { try { return localStorage.getItem('chats.deskAlertsAsked') === '1'; } catch { return false; } });
  const deskPrompt = !isPhone && !preview && notify === 'default' && !deskAsked;
  function closeDeskPrompt() {
    setDeskAsked(true);
    try { localStorage.setItem('chats.deskAlertsAsked', '1'); } catch { /* storage blocked: asks again next visit */ }
  }

  const listHeadRows = (
    <>
      <div className="chats-listhead-row">
        <YouButton />
        <h1 className="chats-listhead-title">Chats</h1>
        {!liveData && <span className="chats-sample">Sample data</span>}
        {/* Switch to Sentinel: the open chat's customer or lead, or Sentinel's home (Gary, 18 Oct). On a phone it is in
            Settings, behind your picture (Gary, 3 Nov 2026). */}
        <button type="button" className="chats-listhead-help chats-listhead-desk" aria-label="Switch to Sentinel" title="Switch to Sentinel"
          onClick={() => { const path = sentinelPathFor(chats.find((c) => c.id === openId)); if (liveData) void openInSentinel(path); else window.open(portalUrl(path), SENTINEL_WINDOW); }}><AppsIcon /></button>
        <DutySwitch />
        <ReportButton />
        <AskButton />
      </div>
      <div className="chats-searchrow">
        <button type="button" className="chats-searchbox" onClick={() => setSearch('')}>
          <SearchIcon />
          <span className="chats-searchbox-text">Search name, number or message</span>
        </button>
        <button type="button" className={`chats-icon-btn chats-filter-btn${filterOn ? ' chats-filter-btn--on' : ''}`} aria-label="Filter chats" title="Filter chats"
          onClick={() => openSheet(<FilterSheet chats={unfiltered} pairs={pairs} />)}>
          <FilterIcon />
          {filterOn && <span className="chats-badge chats-badge--sm chats-chip-count">{filterCount(listFilter)}</span>}
        </button>
      </div>
    </>
  );

  return (
    <>
      {selection.selecting ? (
        // The bar sits over the header it replaces, at its height, so the tabs never jump up (mobile review, 19 Oct).
        <div className="chats-listhead chats-listhead--select">
          <div className="chats-listhead-ghost">{listHeadRows}</div>
          <SelectionBar selection={selection} chats={list} shown={list} />
        </div>
      ) : search !== null ? (
        <header className="chats-topbar chats-topbar--search">
          <button type="button" className="chats-icon-btn" aria-label="Close search" onClick={() => setSearch(null)}><BackIcon /></button>
          <input className="chats-search-input" type="search" enterKeyHint="search" onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, number or message" aria-label="Search chats" />
        </header>
      ) : (
        // As the agreed mock-up (Gary, 15 Oct): a large "Chats" title with the search box under it.
        <header className="chats-listhead">
          {listHeadRows}
        </header>
      )}

      {deskPrompt && search === null && (
        <div className="chats-banner chats-banner--desk">
          <b>Get alerts on this computer</b>
          <span>New messages pop up even when this tab is in the background.</span>
          <span className="chats-banner-actions">
            <button type="button" className="chats-banner-btn" onClick={() => void askNotifications().then(closeDeskPrompt)}>Turn on</button>
            <button type="button" className="chats-banner-later" onClick={closeDeskPrompt}>Not now</button>
          </span>
        </div>
      )}
      {phoneProblem && search === null && (
        EMBEDDED ? (
          // Inside Sentinel a phone cannot get alerts: the stand-alone app can (Gary, 19 Oct: Debbie).
          <a className="chats-banner" href={STANDALONE_CHATS_URL} target="_blank" rel="noopener">
            <b>For alerts on this phone, open CMB Chats on its own</b>
            <span>Tap to open it, then add it to your home screen.</span>
          </a>
        ) : (
          <button type="button" className="chats-banner" onClick={() => navigate('/settings')}>
            <b>{!standalone ? 'Open CMB Chats from your home screen' : 'Notifications are off on this phone'}</b>
            <span>New leads will not reach you. Tap to fix.</span>
          </button>
        )
      )}


      {search === null && filterOn && (
        <div className="chats-filter-active" aria-label="Filter">
          {(['brand', 'status', 'owner', 'area', 'label'] as const).flatMap((g) => listFilter[g].map((v) => (
            <button key={`${g}-${v}`} type="button" className="chats-filter-pill" aria-label={`Remove ${v}`} onClick={narrow(() => toggleFilter(g, v))}>
              {v}<CloseIcon />
            </button>
          )))}
          <button type="button" className="chats-select-all" onClick={narrow(clearFilter)}>Clear</button>
        </div>
      )}

      <main ref={listScroll} className="chats-screen">
        {/* As WhatsApp: Archived is the first row of the list and scrolls away with it. */}
        {search === null && reminders.length > 0 && (
          <button type="button" className="chats-archived-row" onClick={() => navigate('/reminders')}>
            <span className="chats-archived-icon"><BellIcon /></span>
            <span className="chats-archived-label">Reminders</span>
            <span className={`chats-archived-count${remindersDue ? ' chats-archived-count--hot' : ''}`}>{remindersDue ? `${remindersDue} due` : reminders.length}</span>
          </button>
        )}
        {search === null && archived.length > 0 && (
          <button type="button" className="chats-archived-row" onClick={() => navigate('/archived')}>
            <span className="chats-archived-icon"><ArchiveIcon /></span>
            <span className="chats-archived-label">Archived</span>
            {/* The number of rows in Archived (Gary, 19 Oct: it showed unread, 5 against 11 rows). */}
            <span className="chats-archived-count">{onePerCustomer(archived, channelPairs(archived), now).length}</span>
          </button>
        )}
        {list.length === 0 && (search === null && filterOn
          ? <p className="chats-empty">No chats match this filter. <button type="button" className="chats-select-all" onClick={clearFilter}>Clear filter</button></p>
          : <p className="chats-empty">{search ? 'No chats match.' : 'Nothing here. You are all caught up.'}</p>)}
        {/* One row of filters (Gary, 18 Oct): the CMB and POS row went; the brand dot on each picture tells them apart. */}
        <ChatRows chats={list} selection={selection} showBrand markOld={!!search?.trim()} />
      </main>
      {selection.selecting && <span className="chats-sr" role="status">{selection.ids.length} selected</span>}
    </>
  );
}
